// The viewer's own words, in every language the app supports. The interface follows the
// visitor's language; the content language is picked separately from what the scene offers.

const en = {
  loading: "Loading…",
  notFoundTitle: "Nothing here yet",
  notFoundBody:
    "This code isn't linked to a published experience. If you scanned a card, it may have been taken offline.",
  teamDraft: "Working on this project? Open the draft preview",
  loadFailed: "Couldn't load the experience. Check your connection and try again.",
  retry: "Try again",
  "intro.card": "Point your camera at the printed card and watch it come alive.",
  "intro.world": "Point at the card's QR code to place the scene, then walk all the way around it.",
  "intro.floor": "Place the scene on a table or the floor in your room.",
  "intro.wall": "Hang the scene on a wall in your room.",
  "intro.3d": "Explore it in 3D. Drag to turn it, pinch to zoom.",
  "steps.card.1": "Lay the card flat in good light",
  "steps.card.2": "Hold your phone 30–40 cm above it",
  "steps.card.3": "Tap things to explore",
  "steps.world.1": "Put the card on a table",
  "steps.world.2": "Point at its QR code until it locks",
  "steps.world.3": "Walk around the scene",
  "steps.floor.1": "Find a clear table or floor",
  "steps.floor.2": "Move your phone slowly",
  "steps.floor.3": "Tap to place, pinch to resize",
  "steps.wall.1": "Stand a few steps from a wall",
  "steps.wall.2": "Move your phone slowly across it",
  "steps.wall.3": "Tap to hang it there",
  start: "Start",
  starting: "Starting the camera…",
  view3d: "View in 3D",
  privacy: "The camera picture stays on your phone. Nothing is recorded or sent.",
  searching: "Looking for the card…",
  scanHint: "Fit the card inside the frame",
  found: "Card found",
  stop: "Close",
  back: "Back",
  share: "Share",
  copied: "Link copied",
  language: "العربية",
  dragHint: "Drag to turn · Pinch or scroll to zoom",
  desktopTitle: "Open it on your phone",
  desktopBody: "Scan this code with your phone's camera.",
  "desktop.card": "Then point the phone at the printed card.",
  "desktop.world": "Then point it at the card's QR code and walk around the scene.",
  "desktop.floor": "Then place the scene on a table or the floor.",
  "desktop.wall": "Then hang the scene on a wall.",
  update: "This experience needs a newer viewer. Reload the page.",
  "xr.scanning": "Move your phone slowly to find a surface",
  "xr.scanningWall": "Point at a wall and move your phone slowly",
  "xr.place": "Tap to place it here",
  "xr.placed": "Pinch to resize · Twist to turn · Tap things to explore",
  "xr.aim": "Point at the QR code on your card",
  "xr.locked": "Locked to your card. Walk around!",
  "xr.manual": "Put the circle on the centre of your card, then tap",
  "xr.again": "Place again",
  "xr.tapInstead": "Can't find the code? Tap the card instead",
  "xr.exit": "Exit",
  "xr.noRoom": "This phone can't place things in the room, so here it is in 3D.",
  "errors.insecure": "The camera needs a secure (https) page.",
  "errors.noCamera": "No camera was found on this device.",
  "errors.denied": "Camera access was blocked. Allow it in your browser settings, then try again.",
  "errors.failed": "The camera couldn't start. Try again, or view in 3D.",
  "leaving.title": "You're leaving this experience",
  "leaving.body": "This link opens another website: {host}",
  "leaving.children": "Ask a grown-up before you continue.",
  "leaving.go": "Continue",
  "leaving.stay": "Stay here",
  madeWith: "Made with ARCraft Studio",
  report: "Report",
  "collection.count": "{count} experiences",
  "collection.open": "Open",
  "collection.empty": "Nothing is published in this collection yet.",
  "collection.notFound": "This collection isn't available.",
  "badge.card": "On the card",
  "badge.world": "Walk around",
  "badge.floor": "In your room",
  "badge.wall": "On a wall",
  "badge.3d": "3D",
};

export type MessageKey = keyof typeof en;

const ar: Record<MessageKey, string> = {
  loading: "جارٍ التحميل…",
  notFoundTitle: "لا يوجد شيء هنا بعد",
  notFoundBody: "هذا الرمز غير مرتبط بتجربة منشورة. إذا مسحت كارتًا، فربما أُوقف نشره.",
  teamDraft: "تعمل على هذا المشروع؟ افتح معاينة المسودة",
  loadFailed: "تعذّر تحميل التجربة. تحقق من الاتصال وحاول مرة أخرى.",
  retry: "إعادة المحاولة",
  "intro.card": "وجّه الكاميرا نحو الكارت المطبوع وشاهده ينبض بالحياة.",
  "intro.world": "وجّه الهاتف نحو رمز QR على الكارت لوضع المشهد، ثم تجوّل حوله.",
  "intro.floor": "ضع المشهد على طاولة أو على الأرض في غرفتك.",
  "intro.wall": "علّق المشهد على حائط في غرفتك.",
  "intro.3d": "استكشفها بالأبعاد الثلاثية. اسحب للتدوير، وقرّب بإصبعين.",
  "steps.card.1": "ضع الكارت مسطحًا في إضاءة جيدة",
  "steps.card.2": "أمسك الهاتف على بعد 30–40 سم فوقه",
  "steps.card.3": "اضغط على العناصر لتستكشف",
  "steps.world.1": "ضع الكارت على طاولة",
  "steps.world.2": "وجّه الهاتف نحو رمز QR حتى يثبت",
  "steps.world.3": "تجوّل حول المشهد",
  "steps.floor.1": "ابحث عن طاولة أو أرضية خالية",
  "steps.floor.2": "حرّك هاتفك ببطء",
  "steps.floor.3": "اضغط للوضع، وقرّب بإصبعين لتغيير الحجم",
  "steps.wall.1": "قف على بعد خطوات من حائط",
  "steps.wall.2": "حرّك هاتفك ببطء عليه",
  "steps.wall.3": "اضغط لتعليقه هناك",
  start: "ابدأ",
  starting: "جارٍ تشغيل الكاميرا…",
  view3d: "عرض ثلاثي الأبعاد",
  privacy: "تبقى صورة الكاميرا على هاتفك. لا يُسجَّل شيء ولا يُرسَل.",
  searching: "جارٍ البحث عن الكارت…",
  scanHint: "ضع الكارت داخل الإطار",
  found: "تم العثور على الكارت",
  stop: "إغلاق",
  back: "رجوع",
  share: "مشاركة",
  copied: "تم نسخ الرابط",
  language: "English",
  dragHint: "اسحب للتدوير · قرّب بإصبعين أو بعجلة الفأرة",
  desktopTitle: "افتحها على هاتفك",
  desktopBody: "امسح هذا الرمز بكاميرا هاتفك.",
  "desktop.card": "ثم وجّه الهاتف نحو الكارت المطبوع.",
  "desktop.world": "ثم وجّهه نحو رمز QR على الكارت وتجوّل حول المشهد.",
  "desktop.floor": "ثم ضع المشهد على طاولة أو على الأرض.",
  "desktop.wall": "ثم علّق المشهد على حائط.",
  update: "تحتاج هذه التجربة إلى عارض أحدث. أعد تحميل الصفحة.",
  "xr.scanning": "حرّك هاتفك ببطء للعثور على سطح",
  "xr.scanningWall": "وجّه الهاتف نحو حائط وحرّكه ببطء",
  "xr.place": "اضغط لوضعه هنا",
  "xr.placed": "قرّب بإصبعين لتغيير الحجم · لُفّ للتدوير · اضغط لتستكشف",
  "xr.aim": "وجّه الهاتف نحو رمز QR على الكارت",
  "xr.locked": "ثبت على الكارت. تجوّل حوله!",
  "xr.manual": "ضع الدائرة على منتصف الكارت ثم اضغط",
  "xr.again": "ضعه من جديد",
  "xr.tapInstead": "لا تجد الرمز؟ اضغط على الكارت بدلًا منه",
  "xr.exit": "خروج",
  "xr.noRoom": "لا يستطيع هذا الهاتف وضع الأشياء في الغرفة، لذا إليك العرض ثلاثي الأبعاد.",
  "errors.insecure": "تحتاج الكاميرا إلى صفحة آمنة (https).",
  "errors.noCamera": "لم يتم العثور على كاميرا في هذا الجهاز.",
  "errors.denied": "تم حظر الوصول إلى الكاميرا. اسمح به من إعدادات المتصفح ثم حاول مرة أخرى.",
  "errors.failed": "تعذّر تشغيل الكاميرا. حاول مرة أخرى أو اعرضها ثلاثية الأبعاد.",
  "leaving.title": "أنت على وشك مغادرة هذه التجربة",
  "leaving.body": "يفتح هذا الرابط موقعًا آخر: {host}",
  "leaving.children": "اسأل شخصًا بالغًا قبل المتابعة.",
  "leaving.go": "متابعة",
  "leaving.stay": "البقاء هنا",
  madeWith: "صُنعت باستخدام ARCraft Studio",
  report: "إبلاغ",
  "collection.count": "{count} تجربة",
  "collection.open": "فتح",
  "collection.empty": "لم يُنشر شيء في هذه المجموعة بعد.",
  "collection.notFound": "هذه المجموعة غير متاحة.",
  "badge.card": "على الكارت",
  "badge.world": "تجوّل حوله",
  "badge.floor": "في غرفتك",
  "badge.wall": "على الحائط",
  "badge.3d": "ثلاثي الأبعاد",
};

export const MESSAGES = { en, ar } as const;
export type UiLanguage = keyof typeof MESSAGES;

/** The interface language for a locale: Arabic for any Arabic variant, else English. */
export function uiLanguage(locale: string): UiLanguage {
  return locale.split("-")[0] === "ar" ? "ar" : "en";
}

/** The first of the visitor's languages the interface speaks, else English. */
export function pickUiLanguage(preferred: readonly string[]): UiLanguage {
  for (const wanted of preferred) {
    const primary = wanted.split("-")[0];
    if (primary && primary in MESSAGES) return primary as UiLanguage;
  }
  return "en";
}

export function translate(lang: UiLanguage, key: MessageKey, vars: Record<string, string> = {}) {
  return MESSAGES[lang][key].replace(/\{(\w+)\}/g, (_, name: string) => vars[name] ?? "");
}

/**
 * Which of the scene's languages this visitor gets: an explicit `?lang=`, then the browser's
 * languages (exact, then by primary language), then the scene's default.
 */
export function pickLocale(available: readonly string[], fallback: string, preferred: readonly string[]) {
  for (const wanted of preferred) {
    if (available.includes(wanted)) return wanted;
    const primary = wanted.split("-")[0];
    const match = available.find((locale) => locale.split("-")[0] === primary);
    if (match) return match;
  }
  return available.includes(fallback) ? fallback : (available[0] ?? fallback);
}
